import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type DefaultMessagesResponse,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import type { EligibleMessage as BackendMessageBody } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { SerializableRecord } from '@post-office/serializable-record';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useCallback, useEffect, useState } from 'react';

type CustomDismissCallback = () => void;

export type PlacementRenderProps = {
	dismiss: CustomDismissCallback;
	onItemClick?: () => void;
	onItemViewed?: () => void;
	isSelected?: boolean;
};

type placementDefinition = CustomRenderer<PlacementRenderProps>;

export type PlacementFrontendProps = {
	preloadedInitialData?: DefaultMessagesResponse | undefined;
	onMessageCountChange?: (numberOfMessages: number) => void;
	onItemClick?: () => void;
	onItemViewed?: () => void;
	isSelected?: boolean;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: 'jsm-assets-app-navbar-data-manager-section',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
};

const placement = {
	createFrontend: createPlacementFrontend<placementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>(
	(Messages) =>
		({ onMessageCountChange, preloadedInitialData, onItemClick, onItemViewed, isSelected }) => {
			const [{ messages, status }, setMessagesState] = useState<{
				messages: BackendMessageBody<SerializableRecord>[] | undefined;
				status: 'loading' | 'done';
			}>(() => {
				if (preloadedInitialData) {
					return preloadedInitialData?.messages?.length > 0
						? {
								status: 'done',
								messages: preloadedInitialData?.messages,
							}
						: { messages: undefined, status: 'done' };
				}

				return { messages: undefined, status: 'loading' };
			});

			const isDone = status === 'done';

			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				autoRefetch: {
					isEnabled: !isDone,
					updateKey: 'your-placement-id',
				},
			});

			useEffect(() => {
				if (typeof messages === 'undefined') {
					return;
				}

				if (!onMessageCountChange) {
					return;
				}

				onMessageCountChange(messages.length);
			}, [messages, onMessageCountChange]);

			const handleDismiss = useCallback(
				(messageInstanceId: string): void => {
					setMessagesState(() => {
						if (!messages) {
							return { messages: undefined, status: 'done' };
						}

						const filteredMessages = messages.filter(
							(message) => messageInstanceId !== message.messageInstanceId,
						);

						return {
							messages: filteredMessages,
							status: 'done',
						};
					});
				},
				[messages],
			);

			useEffect(() => {
				if (!isLoading && !isDone) {
					setMessagesState({
						messages: data?.messages,
						status: 'done',
					});
				}
			}, [isLoading, isDone, data]);

			if (error) {
				return null;
			}

			if (status === 'loading') {
				return null;
			}

			return Array.isArray(messages) && messages.length > 0 ? (
				<>
					{messages?.map((props, index) => (
						<Messages.render
							key={`${props.messageInstanceId}-${index}`}
							dismiss={() => handleDismiss(props.messageInstanceId)}
							onItemClick={onItemClick}
							onItemViewed={onItemViewed}
							isSelected={isSelected}
							{...props}
						/>
					))}
				</>
			) : null;
		},
);
